import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Kanban, dropHandler } from "react-kanban-kit";
import type { LateralCandidate, LateralJobPosition } from "@/types/database";
import { MessageSquare, Briefcase, RefreshCw, Filter, X, LayoutGrid, Check } from "lucide-react";
import {
  LATERAL_KANBAN_COLUMNS as ALL_COLUMNS,
  LATERAL_STATUS_OPTIONS,
  LATERAL_STATUS_COLORS,
} from "@/lib/lateral-statuses";
import {
  LateralFilterBar,
  LateralFilterValues,
  emptyLateralFilters,
} from "@/components/lateral/LateralFilterBar";
import { LateralSavedFiltersBar } from "@/components/lateral/LateralSavedFiltersBar";

type BoardData = Record<string, any>;

const PAGE_SIZE = 20;
const LS_KEY = "lateral_kanban_visible_columns";

// Sensible default: active pipeline statuses only
const DEFAULT_VISIBLE = [
  "applied",
  "ai_rejected",
  "shortlisted",
  "interview_scheduled",
  "hired",
  "on_hold",
  "rejected",
];

function getStoredColumns(): string[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed as string[];
    }
  } catch {}
  return DEFAULT_VISIBLE;
}

function toCardNode(c: LateralCandidate, colId: string) {
  return {
    id: c.id,
    title: c.full_name,
    type: "candidate",
    parentId: colId,
    children: [] as string[],
    totalChildrenCount: 0,
    content: c,
  };
}

function buildInitialBoard(
  columnData: Array<{ colId: string; candidates: LateralCandidate[]; total: number }>,
  columns: typeof ALL_COLUMNS
): BoardData {
  const board: BoardData = {
    root: {
      id: "root",
      title: "Root",
      children: columns.map((c) => c.id),
      totalChildrenCount: columns.length,
      parentId: null,
    },
  };
  for (const { colId, candidates, total } of columnData) {
    const col = columns.find((c) => c.id === colId)!;
    board[colId] = {
      id: colId,
      title: col.title,
      children: candidates.map((c) => c.id),
      totalChildrenCount: total,
      parentId: "root",
    };
    for (const c of candidates) {
      board[c.id] = toCardNode(c, colId);
    }
  }
  return board;
}

function ScoreBadge({ score }: { score: number | null | undefined }) {
  if (score == null) return <span className="text-xs text-gray-400">No score</span>;
  const color =
    score >= 70 ? "text-green-700 bg-green-50"
    : score >= 50 ? "text-yellow-700 bg-yellow-50"
    : "text-red-700 bg-red-50";
  return (
    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${color}`}>
      {score}/100
    </span>
  );
}

function NotePopover({
  candidateId, currentNote, onSaved,
}: {
  candidateId: string; currentNote: string | null | undefined; onSaved: (note: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(currentNote ?? "");
  const { toast } = useToast();

  const save = async () => {
    const { error } = await supabase
      .from("lateral_candidates")
      .update({ notes: value, updated_at: new Date().toISOString() })
      .eq("id", candidateId);
    if (error) {
      toast({ title: "Failed to save note", variant: "destructive" });
    } else {
      onSaved(value);
      setOpen(false);
      toast({ title: "Note saved" });
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setValue(currentNote ?? ""); }}>
      <PopoverTrigger asChild>
        <button
          className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded hover:bg-gray-100 transition-colors ${
            currentNote ? "text-blue-600" : "text-gray-400"
          }`}
          title={currentNote ? "Edit note" : "Add note"}
          onClick={(e) => e.stopPropagation()}
        >
          <MessageSquare className="h-3 w-3" />
          {currentNote ? "Note" : "Add note"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" onClick={(e) => e.stopPropagation()}>
        <p className="text-xs font-medium text-gray-700 mb-2">Note</p>
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="e.g. Called, no answer. Try again Friday."
          rows={4}
          className="text-sm resize-none"
        />
        <div className="flex justify-end gap-2 mt-2">
          <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" onClick={save}>Save</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ColHeader({ column }: { column: any }) {
  const loaded: number = column.children?.length ?? 0;
  const total: number = column.totalChildrenCount ?? 0;
  return (
    <div className="flex items-center gap-2 px-1 py-1 w-full">
      <span className="font-semibold text-sm text-gray-800 flex-1">{column.title}</span>
      <span className="text-xs text-gray-400 tabular-nums">
        {loaded < total ? `${loaded} / ${total}` : total}
      </span>
    </div>
  );
}

export default function LateralKanbanPage() {
  const { positionId } = useParams<{ positionId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [dataSource, setDataSource] = useState<BoardData>({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Which columns are visible — persisted to localStorage
  const [visibleColumns, setVisibleColumns] = useState<string[]>(getStoredColumns);

  // Active columns in display order (derived, kept in a ref so loadInitial can read it without a dependency)
  const activeColumns = useMemo(
    () => ALL_COLUMNS.filter((c) => visibleColumns.includes(c.id)),
    [visibleColumns]
  );
  const activeColumnsRef = useRef(activeColumns);
  useEffect(() => { activeColumnsRef.current = activeColumns; }, [activeColumns]);

  const toggleColumn = (id: string) => {
    setVisibleColumns((prev) => {
      const next = prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id];
      if (next.length === 0) return prev; // always keep at least one
      localStorage.setItem(LS_KEY, JSON.stringify(next));
      return next;
    });
  };

  // Draft filters (UI) vs applied filters (queries) — ref pattern avoids stale closures
  const [draftFilters, setDraftFilters] = useState<LateralFilterValues>(emptyLateralFilters());
  const appliedFiltersRef = useRef<LateralFilterValues>(emptyLateralFilters());
  const [appliedFilterCount, setAppliedFilterCount] = useState(0);

  // Stale-closure-safe refs
  const dsRef = useRef<BoardData>({});
  const loadingCols = useRef<Set<string>>(new Set());
  const pageRef = useRef<Record<string, number>>({});
  const positionIdRef = useRef(positionId);

  useEffect(() => { dsRef.current = dataSource; }, [dataSource]);
  useEffect(() => { positionIdRef.current = positionId; }, [positionId]);

  const { data: position } = useQuery({
    queryKey: ["lateral-job-position", positionId],
    queryFn: async () => {
      if (!positionId) return null;
      const { data } = await supabase
        .from("lateral_job_positions")
        .select("id, job_title")
        .eq("id", positionId)
        .single();
      return data as Pick<LateralJobPosition, "id" | "job_title"> | null;
    },
    enabled: !!positionId,
  });

  function applyFilters(q: any) {
    const f = appliedFiltersRef.current;
    if (f.minScore !== "") q = q.gte("cv_score", Number(f.minScore));
    if (f.maxScore !== "") q = q.lte("cv_score", Number(f.maxScore));
    if (f.minExp !== "") q = q.gte("total_experience_years", Number(f.minExp));
    if (f.maxExp !== "") q = q.lte("total_experience_years", Number(f.maxExp));
    if (f.locationFilter.trim()) q = q.ilike("current_location", `%${f.locationFilter.trim()}%`);
    if (f.noticePeriodFilter.trim()) q = q.ilike("notice_period", `%${f.noticePeriodFilter.trim()}%`);
    return q;
  }

  const loadInitial = useCallback(async () => {
    const pid = positionIdRef.current;
    const cols = activeColumnsRef.current;
    if (!pid || cols.length === 0) return;
    setIsInitialLoading(true);
    pageRef.current = {};
    loadingCols.current = new Set();

    try {
      const results = await Promise.all(
        cols.map(async (col) => {
          let q = supabase
            .from("lateral_candidates")
            .select("*", { count: "exact" })
            .eq("status", col.id)
            .eq("lateral_job_position_id", pid)
            .order("updated_at", { ascending: false })
            .limit(PAGE_SIZE);
          q = applyFilters(q);
          const { data, count, error } = await q;
          if (error) throw error;
          return { colId: col.id, candidates: (data ?? []) as LateralCandidate[], total: count ?? 0 };
        })
      );

      results.forEach(({ colId }) => { pageRef.current[colId] = 1; });
      setDataSource(buildInitialBoard(results, cols));
      setTotalCandidates(results.reduce((sum, r) => sum + r.total, 0));
    } catch (err: any) {
      toast({ title: "Failed to load board", description: err.message, variant: "destructive" });
    } finally {
      setIsInitialLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  // Reload when position or visible columns change
  useEffect(() => {
    if (positionId) {
      positionIdRef.current = positionId;
      loadInitial();
    }
  }, [positionId, loadInitial]);

  useEffect(() => {
    if (positionId) loadInitial();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleColumns]);

  const loadMore = useCallback((columnId: string) => {
    if (loadingCols.current.has(columnId)) return;
    const col = dsRef.current[columnId];
    if (!col || col.children.length >= col.totalChildrenCount) return;

    loadingCols.current.add(columnId);
    const page = pageRef.current[columnId] ?? 1;

    const doFetch = async () => {
      try {
        let q = supabase
          .from("lateral_candidates")
          .select("*")
          .eq("status", columnId)
          .eq("lateral_job_position_id", positionIdRef.current!)
          .order("updated_at", { ascending: false })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        q = applyFilters(q);
        const { data, error } = await q;
        if (error) throw error;

        const candidates = (data ?? []) as LateralCandidate[];
        pageRef.current[columnId] = page + 1;
        setDataSource((prev) => {
          const prevCol = prev[columnId];
          if (!prevCol) return prev;
          const updated: BoardData = { ...prev };
          updated[columnId] = {
            ...prevCol,
            children: [...prevCol.children, ...candidates.map((c) => c.id)],
          };
          candidates.forEach((c) => { updated[c.id] = toCardNode(c, columnId); });
          return updated;
        });
      } catch {
        // silently ignore
      } finally {
        loadingCols.current.delete(columnId);
      }
    };

    doFetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from("lateral_candidates")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onError: (e: Error) => {
      toast({ title: "Failed to update status", description: e.message, variant: "destructive" });
      loadInitial();
    },
  });

  const handleCardMove = useCallback(
    (move: { cardId: string; fromColumnId: string; toColumnId: string }) => {
      // Allow drop only onto columns in the full list (even if not currently visible)
      if (!ALL_COLUMNS.some((c) => c.id === move.toColumnId)) return;
      setDataSource((prev) => {
        const updated = dropHandler(move, prev, undefined);
        if (move.fromColumnId !== move.toColumnId) {
          updated[move.fromColumnId] = {
            ...updated[move.fromColumnId],
            totalChildrenCount: Math.max(0, (updated[move.fromColumnId]?.totalChildrenCount ?? 0) - 1),
          };
          updated[move.toColumnId] = {
            ...updated[move.toColumnId],
            totalChildrenCount: (updated[move.toColumnId]?.totalChildrenCount ?? 0) + 1,
          };
        }
        return updated;
      });
      if (move.fromColumnId !== move.toColumnId) {
        updateStatus.mutate({ id: move.cardId, status: move.toColumnId });
      }
    },
    [updateStatus]
  );

  const handleApplyFilters = () => {
    appliedFiltersRef.current = { ...draftFilters };
    const count = [
      draftFilters.minScore !== "",
      draftFilters.maxScore !== "",
      draftFilters.minExp !== "",
      draftFilters.maxExp !== "",
      draftFilters.locationFilter.trim() !== "",
      draftFilters.noticePeriodFilter.trim() !== "",
    ].filter(Boolean).length;
    setAppliedFilterCount(count);
    loadInitial();
  };

  const handleClearFilters = () => {
    const empty = emptyLateralFilters();
    setDraftFilters(empty);
    appliedFiltersRef.current = empty;
    setAppliedFilterCount(0);
    loadInitial();
  };

  const configMap = {
    candidate: {
      render: ({ data }: { data: any }) => {
        const c = data.content as LateralCandidate;
        const notes = (data.content?.notes as string) ?? "";
        return (
          <div
            className="bg-white rounded-lg border shadow-sm p-3 cursor-pointer hover:shadow-md transition-shadow space-y-2 min-w-0 w-full"
            onClick={() => navigate(`/admin/lateral/candidates/${c.id}/evaluate`)}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="font-medium text-sm text-gray-900 leading-tight">{c.full_name}</p>
              <ScoreBadge score={c.cv_score} />
            </div>
            {(c.current_company || c.current_designation) && (
              <p className="text-xs text-gray-500 truncate">
                {[c.current_designation, c.current_company].filter(Boolean).join(" @ ")}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
              {c.total_experience_years != null && <span>{c.total_experience_years} yrs</span>}
              {c.expected_ctc && <span className="text-blue-600 font-medium">{c.expected_ctc}</span>}
              {c.notice_period && <span className="text-gray-400">{c.notice_period}</span>}
              {c.current_location && <span className="text-gray-400 truncate">{c.current_location}</span>}
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-gray-100">
              <NotePopover
                candidateId={c.id}
                currentNote={notes || null}
                onSaved={(note) =>
                  setDataSource((prev) => ({
                    ...prev,
                    [c.id]: { ...prev[c.id], content: { ...prev[c.id]?.content, notes: note } },
                  }))
                }
              />
              {notes && <p className="text-xs text-gray-400 italic truncate max-w-[120px]">{notes}</p>}
            </div>
          </div>
        );
      },
      isDraggable: true,
    },
  };

  if (!positionId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-3">
        <Briefcase className="h-10 w-10" />
        <p>Select a position from the sidebar to view its Kanban board.</p>
      </div>
    );
  }

  if (isInitialLoading) {
    return <div className="flex items-center justify-center h-64 text-gray-500">Loading board...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {position?.job_title ?? "Kanban Board"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {totalCandidates} candidates · {activeColumns.length} of {ALL_COLUMNS.length} columns shown
            {appliedFilterCount > 0 && (
              <span className="ml-2 text-blue-600">
                · {appliedFilterCount} filter{appliedFilterCount > 1 ? "s" : ""} active
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Columns picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <LayoutGrid className="h-4 w-4" />
                Columns
                <span className="text-xs text-gray-400">
                  {activeColumns.length}/{ALL_COLUMNS.length}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="end">
              <p className="text-xs font-medium text-gray-500 px-2 py-1 mb-1">Visible columns</p>
              {LATERAL_STATUS_OPTIONS.map((s) => {
                const isVisible = visibleColumns.includes(s.value);
                return (
                  <button
                    key={s.value}
                    className="flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-gray-50 transition-colors text-left"
                    onClick={() => toggleColumn(s.value)}
                  >
                    <span
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                        isVisible
                          ? "bg-blue-500 border-blue-500"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {isVisible && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                    </span>
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                        LATERAL_STATUS_COLORS[s.value] ?? "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {s.label}
                    </span>
                  </button>
                );
              })}
              <div className="border-t mt-2 pt-2 px-2 flex gap-2">
                <button
                  className="text-xs text-blue-600 hover:underline"
                  onClick={() => {
                    const all = ALL_COLUMNS.map((c) => c.id);
                    setVisibleColumns(all);
                    localStorage.setItem(LS_KEY, JSON.stringify(all));
                  }}
                >
                  Show all
                </button>
                <span className="text-gray-300">·</span>
                <button
                  className="text-xs text-gray-500 hover:underline"
                  onClick={() => {
                    setVisibleColumns(DEFAULT_VISIBLE);
                    localStorage.setItem(LS_KEY, JSON.stringify(DEFAULT_VISIBLE));
                  }}
                >
                  Reset
                </button>
              </div>
            </PopoverContent>
          </Popover>

          {/* Filters */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters((v) => !v)}
            className="gap-1.5"
          >
            <Filter className="h-4 w-4" />
            Filters
            {appliedFilterCount > 0 && (
              <span className="bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {appliedFilterCount}
              </span>
            )}
          </Button>
          {appliedFilterCount > 0 && (
            <Button variant="ghost" size="sm" onClick={handleClearFilters} className="gap-1 text-gray-500 h-8">
              <X className="h-3.5 w-3.5" />
              Clear
            </Button>
          )}

          <Button variant="outline" size="sm" onClick={loadInitial} className="gap-1.5">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Saved filters row — always visible */}
      <LateralSavedFiltersBar
        context="kanban"
        currentFilters={draftFilters}
        onApply={(saved) => {
          const base = emptyLateralFilters();
          const merged: LateralFilterValues = { ...base };
          Object.keys(base).forEach((k) => {
            const key = k as keyof LateralFilterValues;
            if (saved[key] !== undefined) merged[key] = saved[key];
          });
          setDraftFilters(merged);
          appliedFiltersRef.current = merged;
          const count = [
            merged.minScore !== "",
            merged.maxScore !== "",
            merged.minExp !== "",
            merged.maxExp !== "",
            merged.locationFilter.trim() !== "",
            merged.noticePeriodFilter.trim() !== "",
          ].filter(Boolean).length;
          setAppliedFilterCount(count);
          loadInitial();
        }}
        onClear={handleClearFilters}
      />

      {showFilters && (
        <LateralFilterBar
          values={draftFilters}
          onChange={(key, value) => setDraftFilters((f) => ({ ...f, [key]: value }))}
          positions={[]}
          showStatus={false}
          showPosition={false}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        />
      )}

      <div className="overflow-x-auto pb-4">
        <Kanban
          dataSource={dataSource}
          configMap={configMap}
          renderColumnHeader={(column) => <ColHeader column={column} />}
          onCardMove={handleCardMove}
          loadMore={loadMore}
          virtualization={true}
          cardsGap={6}
        />
      </div>

      {totalCandidates === 0 && (
        <div className="flex flex-col items-center justify-center h-48 text-gray-400">
          <p>
            {appliedFilterCount > 0
              ? "No candidates match the applied filters."
              : "No candidates in the visible columns for this position."}
          </p>
          {appliedFilterCount === 0 && (
            <Button className="mt-3" variant="outline" onClick={() => navigate("/admin/lateral/candidates/create")}>
              Add Candidate
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
